import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  Box, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress,
  Checkbox, FormControlLabel, Link
} from '@mui/material';
import { Visibility, VisibilityOff, Home } from '@mui/icons-material';
import { login, clearError } from '../store/slices/authSlice';
import { setBusinessUnit } from '../store/slices/systemSlice';
import { getHomePathForRoles, getPreferredSystem } from '../utils/roles';
import logo from '../assets/logo.png';

// ---- DDIG brand tokens (kept in sync with the marketing site) ----
const NAVY = '#0B2545';
const NAVY_DEEP = '#071A33';
const GOLD = '#C9A227';
const GOLD_LIGHT = '#E4C567';
const IVORY = '#F7F5EE';
const INK = '#10182B';
const SLATE = '#5B6472';

const DISPLAY_FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const BUSINESS_LINES = ['Automotive', 'Coffee', 'Logistics', 'Investment'];

const LoginPage = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector((s) => s.auth);
  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    if (isAuthenticated && user?.roles) {
      const preferredSystem = getPreferredSystem(user.roles);
      dispatch(setBusinessUnit(preferredSystem));
      navigate(getHomePathForRoles(user.roles, preferredSystem), { replace: true });
    }
    return () => dispatch(clearError());
  }, [dispatch, isAuthenticated, navigate, user]);

  const onSubmit = async (data) => {
    const result = await dispatch(login(data));
    if (login.fulfilled.match(result)) {
      const preferredSystem = getPreferredSystem(result.payload.user?.roles || []);
      dispatch(setBusinessUnit(preferredSystem));
      navigate(getHomePathForRoles(result.payload.user?.roles || [], preferredSystem), { replace: true });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        fontFamily: BODY_FONT,
        '@keyframes fadeUp': {
          '0%': { opacity: 0, transform: 'translateY(14px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes ruleGrow': {
          '0%': { width: 0, opacity: 0 },
          '100%': { width: '64px', opacity: 1 },
        },
        '@keyframes driftSlow': {
          '0%': { transform: 'translate(0, 0)' },
          '100%': { transform: 'translate(-40px, -30px)' },
        },
      }}
    >
      {/* ============ LEFT — BRAND PANEL ============ */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', md: '52%' },
          minHeight: { xs: 260, md: '100vh' },
          background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY} 62%, #163a6e 100%)`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'center', md: 'space-between' },
          px: { xs: 4, md: 8 },
          py: { xs: 5, md: 8 },
          overflow: 'hidden',
        }}
      >
        {/* faint trade-route line pattern, signature texture */}
        <Box
          component="svg"
          viewBox="0 0 800 800"
          sx={{
            position: 'absolute',
            top: '-10%',
            left: '-10%',
            width: '130%',
            height: '130%',
            opacity: 0.07,
            animation: 'driftSlow 40s ease-in-out infinite alternate',
            pointerEvents: 'none',
          }}
        >
          <path d="M0,600 C150,500 250,650 400,550 C550,450 650,580 800,480" stroke={GOLD_LIGHT} strokeWidth="1.5" fill="none" />
          <path d="M0,300 C180,380 300,220 450,300 C600,380 700,240 800,320" stroke={GOLD_LIGHT} strokeWidth="1.5" fill="none" />
          <path d="M-50,150 C120,80 260,180 420,90 C580,0 680,140 850,60" stroke={GOLD_LIGHT} strokeWidth="1" fill="none" />
          <circle cx="400" cy="550" r="4" fill={GOLD_LIGHT} />
          <circle cx="450" cy="300" r="4" fill={GOLD_LIGHT} />
          <circle cx="420" cy="90" r="4" fill={GOLD_LIGHT} />
        </Box>

        {/* huge translucent logo watermark — the dominant visual anchor */}
        <Box
          component="img"
          src={logo}
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '50%',
            left: { xs: '50%', md: '8%' },
            transform: { xs: 'translate(-50%, -50%)', md: 'translateY(-50%)' },
            width: { xs: 420, md: 640 },
            maxWidth: 'none',
            opacity: 0.08,
            filter: 'grayscale(1) brightness(3)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {/* radial glow behind logo */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: -40, md: '38%' },
            left: { xs: '50%', md: 40 },
            transform: { xs: 'translateX(-50%)', md: 'translateY(-50%)' },
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(201,162,39,0.18) 0%, rgba(201,162,39,0) 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* mark + wordmark — big, the actual focal point */}
        <Box sx={{ position: 'relative', animation: 'fadeUp 0.7s ease-out both', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2.5, md: 3.5 }, mb: { xs: 4, md: 7 } }}>
            <Box
              component="img"
              src={logo}
              alt="DDIG Trading PLC"
              sx={{
                width: { xs: 76, md: 108 },
                height: { xs: 76, md: 108 },
                flexShrink: 0,
                filter: 'drop-shadow(0 6px 24px rgba(201,162,39,0.25))',
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: DISPLAY_FONT, fontWeight: 800, color: '#fff',
                  letterSpacing: { xs: 0.2, md: 0.4 }, lineHeight: 1.05,
                  fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.9rem' },
                }}
              >
                DDIG Trading
              </Typography>
              <Typography
                sx={{
                  fontFamily: DISPLAY_FONT, fontWeight: 800, color: GOLD_LIGHT,
                  letterSpacing: { xs: 0.2, md: 0.4 }, lineHeight: 1.05,
                  fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.9rem' },
                }}
              >
                PLC
              </Typography>
              <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', letterSpacing: 3, textTransform: 'uppercase', mt: 1 }}>
                Staff Portal
              </Typography>
            </Box>
          </Box>

          {/* only shown on md+ to keep mobile header compact */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 440 }}>
            <Box sx={{ width: 0, height: 2, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`, mb: 3, animation: 'ruleGrow 0.9s 0.3s ease-out both' }} />
            <Typography
              sx={{
                fontFamily: DISPLAY_FONT, fontWeight: 700, color: '#fff',
                fontSize: '2rem', lineHeight: 1.2, mb: 2.5,
                animation: 'fadeUp 0.7s 0.15s ease-out both',
              }}
            >
              One workspace for sales,
              <br /> inventory and fleet.
            </Typography>
            <Typography
              sx={{
                fontFamily: BODY_FONT, color: 'rgba(255,255,255,0.62)',
                fontSize: '0.98rem', lineHeight: 1.7, maxWidth: 380,
                animation: 'fadeUp 0.7s 0.25s ease-out both',
              }}
            >
              Track stock, log sales and manage operations across every
              DDIG business line from a single, secure sign-in.
            </Typography>
          </Box>
        </Box>

        {/* business lines + footer */}
        <Box sx={{ position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' }, animation: 'fadeUp 0.7s 0.4s ease-out both' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0 18px', mb: 4 }}>
            {BUSINESS_LINES.map((line, i) => (
              <Box key={line} sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.72rem', letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                  {line}
                </Typography>
                {i < BUSINESS_LINES.length - 1 && (
                  <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', background: GOLD, opacity: 0.6 }} />
                )}
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>
            © {new Date().getFullYear()} DDIG Trading PLC — Internal system. Authorized personnel only.
          </Typography>
        </Box>
      </Box>

      {/* ============ RIGHT — SIGN-IN PANEL ============ */}
      <Box
        sx={{
          width: { xs: '100%', md: '48%' },
          flex: 1,
          background: IVORY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3.5, sm: 6 },
          py: { xs: 6, md: 4 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380, animation: 'fadeUp 0.7s 0.15s ease-out both' }}>
          <Button
            onClick={() => navigate('/')}
            startIcon={<Home fontSize="small" />}
            sx={{
              color: NAVY,
              fontFamily: BODY_FONT,
              fontSize: '0.82rem',
              fontWeight: 600,
              textTransform: 'none',
              px: 0,
              mb: { xs: 4, md: 6 },
              '&:hover': { background: 'transparent', color: GOLD },
            }}
          >
            Home
          </Button>
          <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.72rem', letterSpacing: 2.5, textTransform: 'uppercase', color: GOLD, fontWeight: 600, mb: 1.5 }}>
            Sign In
          </Typography>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.6rem', color: INK, mb: 0.75 }}>
            Welcome back
          </Typography>
          <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.9rem', color: SLATE, mb: 4.5 }}>
            Enter your credentials to access your workspace.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: '10px', fontFamily: BODY_FONT, fontSize: '0.85rem' }}
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.78rem', fontWeight: 600, color: INK, mb: 0.75 }}>
                Work email
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                placeholder="you@ddigtrading.com"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{ disableUnderline: false }}
                sx={{
                  fontFamily: BODY_FONT,
                  '& .MuiInput-input': { fontFamily: BODY_FONT, fontSize: '0.95rem', color: INK, py: 1 },
                  '& .MuiInput-underline:before': { borderBottomColor: 'rgba(11,37,69,0.18)' },
                  '& .MuiInput-underline:hover:before': { borderBottomColor: NAVY },
                  '& .MuiInput-underline:after': { borderBottomColor: GOLD, borderBottomWidth: 2 },
                }}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.78rem', fontWeight: 600, color: INK, mb: 0.75 }}>
                Password
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                placeholder="••••••••"
                type={showPassword ? 'text' : 'password'}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Must be at least 6 characters' },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: SLATE }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInput-input': { fontFamily: BODY_FONT, fontSize: '0.95rem', color: INK, py: 1 },
                  '& .MuiInput-underline:before': { borderBottomColor: 'rgba(11,37,69,0.18)' },
                  '& .MuiInput-underline:hover:before': { borderBottomColor: NAVY },
                  '& .MuiInput-underline:after': { borderBottomColor: GOLD, borderBottomWidth: 2 },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{
                      color: 'rgba(11,37,69,0.35)',
                      p: 0.5,
                      '&.Mui-checked': { color: NAVY },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.83rem', color: SLATE }}>
                    Remember me
                  </Typography>
                }
              />
              <Link
                href="#"
                underline="none"
                sx={{
                  fontFamily: BODY_FONT, fontSize: '0.83rem', fontWeight: 600, color: NAVY,
                  '&:hover': { color: GOLD },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.4,
                borderRadius: '10px',
                fontFamily: DISPLAY_FONT,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                letterSpacing: 0.2,
                color: NAVY_DEEP,
                background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                boxShadow: '0 8px 20px rgba(201,162,39,0.28)',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                '&:hover': {
                  background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                  boxShadow: '0 10px 26px rgba(201,162,39,0.38)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-disabled': { background: 'rgba(201,162,39,0.35)', color: 'rgba(7,26,51,0.5)' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: NAVY_DEEP }} /> : 'Sign In'}
            </Button>
          </form>

          <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.78rem', color: SLATE, textAlign: 'center', mt: 4 }}>
            Trouble signing in? Contact your{' '}
            <Box component="span" sx={{ color: NAVY, fontWeight: 600 }}>
              IT Infrastructure
            </Box>{' '}
            administrator.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
